import { takeLatest, put, call, select } from 'redux-saga/effects';
import WorkflowService, {
  IGetCurrentWorkflowResponse,
  IStartWorkflowResponse,
  ISubmitAnswerResponse
} from 'shared/services/WorkflowService';
import {
  getCurrentWorkflow,
  setCurrentWorkflow,
  getCurrentWorkflowFail,
  startWorkflowExecution,
  setWorkflowExecution,
  startWorkflowExecutionFail,
  submitAnswer,
  setAnswerResponse,
  submitAnswerFail
} from './workflow.redux';
import { TAction } from 'shared/types';
import { IGetCurrentWorkflowParams } from 'shared/services/WorkflowService';
import { IState } from 'shared/interfaces/state.interface';

// Selector to get user's affiliate
const getUserAffiliate = (state: IState) =>
  state.user?.data?.patient?.affiliates?.[0]?.id;

function* fetchCurrentWorkflow({
  payload
}: TAction<IGetCurrentWorkflowParams>) {
  try {
    // Get affiliate ID from payload or from user's data
    let affiliateId = payload?.affiliateId;

    if (!affiliateId) {
      // Try to get from user's affiliates
      const userAffiliateId: string = yield select(getUserAffiliate);
      if (userAffiliateId) {
        affiliateId = userAffiliateId;
      }
    }

    const result: IGetCurrentWorkflowResponse = yield call(
      WorkflowService.getCurrentWorkflow,
      {
        affiliateId,
        conversationId: payload?.conversationId
      }
    );

    yield put(setCurrentWorkflow(result));

    // Automatically start the workflow if it exists
    if (result.workflow && result.conversationId) {
      const workflowId = result.workflow.id || result.workflow._id;
      const startNodeId = result.workflow.startNode;

      if (workflowId && startNodeId) {
        yield put(
          startWorkflowExecution({
            workflowId,
            startNodeId,
            conversationId: result.conversationId
          })
        );
      }
    }
  } catch (error: any) {
    yield put(
      getCurrentWorkflowFail(
        error?.response?.data?.message || 'Failed to fetch workflow'
      )
    );
    console.error('fetchCurrentWorkflow error', error);
  }
}

function* startWorkflow({ payload }: TAction<any>): Generator<any, void, any> {
  try {
    const result: IStartWorkflowResponse = yield call(
      WorkflowService.startWorkflow,
      payload.workflowId,
      payload.startNodeId,
      payload.conversationId
    );

    yield put(setWorkflowExecution(result));
  } catch (error: any) {
    yield put(
      startWorkflowExecutionFail(
        error?.response?.data?.message || 'Failed to start workflow'
      )
    );
    console.error('startWorkflow error', error);
  }
}

function* handleSubmitAnswer({
  payload
}: TAction<any>): Generator<any, void, any> {
  try {
    const result: ISubmitAnswerResponse = yield call(
      WorkflowService.submitAnswer,
      payload.executionId,
      payload.nodeId,
      payload.selectedOption,
      payload.isActionNode
    );

    yield put(
      setAnswerResponse({
        ...result,
        userSelection: payload.selectedOption,
        actionType: payload.selectedOption.action?.type
      })
    );
  } catch (error: any) {
    yield put(
      submitAnswerFail(
        error?.response?.data?.message || 'Failed to submit answer'
      )
    );
    console.error('handleSubmitAnswer error', error);
  }
}

function* Workflow() {
  yield takeLatest(getCurrentWorkflow.type, fetchCurrentWorkflow);
  yield takeLatest(startWorkflowExecution.type, startWorkflow);
  yield takeLatest(submitAnswer.type, handleSubmitAnswer);
}

export default Workflow;
